import { Eye, EyeOff } from 'lucide-react'
import type { InputHTMLAttributes } from 'react'
import { forwardRef, useState } from 'react'

import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/cn'

/**
 * A password box with a reveal toggle.
 *
 * Worth the component rather than a `type="password"` and a hand-rolled button on each of
 * the four screens that take a password: the reveal has three details that are easy to get
 * wrong separately and impossible to get wrong once.
 *
 * The toggle is a real `<button type="button">`, so Enter inside the field still submits
 * the form rather than flipping the mask. Its accessible name says what pressing it will
 * do, and `aria-pressed` says what state the field is in now -- a screen-reader user
 * otherwise has no way to know the password is on screen. And it is never disabled while
 * the field is: reading back what you typed is exactly what you want when a form has come
 * back with an error.
 *
 * `autoComplete` is left to the caller. A sign-in box and a change-password box want
 * different values, and guessing wrong here is what makes a browser overwrite a saved
 * password with a new one nobody asked it to keep.
 */
export const PasswordInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function PasswordInput({ className, ...props }, ref) {
    const [revealed, setRevealed] = useState(false)
    const Icon = revealed ? EyeOff : Eye

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={revealed ? 'text' : 'password'}
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setRevealed((on) => !on)}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-pressed={revealed}
          className={cn(
            'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md',
            'text-slate-400 transition-colors hover:text-slate-600',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500',
            'dark:hover:text-slate-200',
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    )
  },
)
